import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Button, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TOUCH_TARGET_PX } from '../../config/touch-target';
import type {
  DeadlineDocumentReading,
  DeadlineReadingStatus,
} from '../../hooks/deadline/use-deadline-document-reading';
import { colors } from '../../theme/ai-theme';
import { AiActDisclaimer } from '../ai-act/ai-act-disclaimer';
import { DocumentFilePill } from './document-file-pill';

/** HEIC resta fuori: il telefono lo converte in JPEG quando il formato non è nell'elenco. */
export const DEADLINE_DOCUMENT_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';

const STATUS_TEST_ID = 'deadline-reading-status';

const STATUS_MESSAGE_KEYS: Partial<Record<DeadlineReadingStatus, string>> = {
  uploading: 'deadlines.aiPreview.uploading',
  reading: 'deadlines.aiPreview.reading',
  failed: 'deadlines.aiPreview.failed',
  timeout: 'deadlines.aiPreview.failed',
  quota: 'deadlines.aiPreview.quota',
  notAttached: 'deadlines.aiPreview.notAttached',
};

interface ReadingStatusLineProps {
  reading: Pick<DeadlineDocumentReading, 'status' | 'rejectionMessage'>;
}

/** Riga informativa discreta, mai rossa: la lettura è un aiuto, il dialog si compila comunque a mano. */
function ReadingStatusLine({ reading }: ReadingStatusLineProps) {
  const { t } = useTranslation('documents');
  const messageKey = STATUS_MESSAGE_KEYS[reading.status];
  if (!messageKey) {
    return null;
  }
  return (
    <Typography
      role="status"
      variant="body2"
      data-testid={STATUS_TEST_ID}
      sx={{ color: 'text.secondary' }}
    >
      {reading.rejectionMessage ?? t(messageKey)}
    </Typography>
  );
}

function isDisclaimerVisible(status: DeadlineReadingStatus): boolean {
  return status === 'reading' || status === 'read';
}

function isBusy(status: DeadlineReadingStatus): boolean {
  return status === 'uploading' || status === 'reading';
}

/**
 * Un file allegato senza lettura (o la cui lettura non è andata) si può ancora far leggere. Mai per
 * un file adottato dall'assistente (`File` vuoto: è solo un token in deposito), a quota finita o
 * per un file che il server ha rifiutato.
 */
function canOfferAiRead(reading: DeadlineDocumentReading, file: File): boolean {
  const isReadable =
    reading.status === 'attached' ||
    reading.status === 'timeout' ||
    (reading.status === 'failed' && reading.rejectionMessage === null);
  return isReadable && file.size > 0 && !reading.aiQuotaExhausted;
}

interface FileInputProps {
  disabled: boolean;
  testId: string;
  onPick: (file: File) => void;
}

/** Annullare la finestra di scelta non tocca il file corrente. */
function HiddenFileInput({ disabled, testId, onPick }: FileInputProps) {
  return (
    <input
      hidden
      type="file"
      accept={DEADLINE_DOCUMENT_ACCEPT}
      disabled={disabled}
      data-testid={testId}
      onChange={(event) => {
        const picked = event.target.files?.[0];
        event.target.value = '';
        if (picked) {
          onPick(picked);
        }
      }}
    />
  );
}

interface UploadButtonProps extends FileInputProps {
  label: string;
  icon: ReactNode;
  hasError: boolean;
  describedBy?: string;
  /** `outlined` nel tema è il pulsante tonale; `text` la pillola con il bordo, per l'alternativa. */
  variant?: 'outlined' | 'text';
}

/**
 * Ogni pulsante chiede un file nuovo: un file già scelto non si rilegge cambiando pulsante. Stessa
 * misura per tutti: a tutta la larghezza della propria colonna, un testo lungo va a capo dentro.
 */
function UploadButton({
  label,
  icon,
  disabled,
  hasError,
  testId,
  describedBy,
  variant = 'outlined',
  onPick,
}: UploadButtonProps) {
  return (
    <Button
      component="label"
      variant={variant}
      color={hasError ? 'error' : 'primary'}
      disabled={disabled}
      startIcon={icon}
      aria-describedby={describedBy}
      fullWidth
      sx={{ minHeight: TOUCH_TARGET_PX, justifyContent: 'center', textAlign: 'center' }}
    >
      {label}
      <HiddenFileInput disabled={disabled} testId={testId} onPick={onPick} />
    </Button>
  );
}

interface ExpiredDocumentNoticeProps {
  required: boolean;
  testIdPrefix: string;
  onContinueWithout: () => void;
}

/**
 * Il server ha rifiutato il token: il file non c'è più. Se il documento è facoltativo si può andare
 * avanti senza; se è obbligatorio resta solo la strada del nuovo caricamento.
 */
function ExpiredDocumentNotice({
  required,
  testIdPrefix,
  onContinueWithout,
}: ExpiredDocumentNoticeProps) {
  const { t } = useTranslation('documents');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
      <Box
        role="alert"
        data-testid={STATUS_TEST_ID}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: colors.accent.amberText }}
      >
        <ErrorOutlineIcon aria-hidden fontSize="small" sx={{ mt: '1px' }} />
        <Typography variant="body2" sx={{ color: 'inherit' }}>
          {required
            ? t('deadlines.aiPreview.expiredRequired', {
                defaultValue:
                  'Il documento non è più disponibile: caricalo di nuovo per continuare.',
              })
            : t('deadlines.aiPreview.expired')}
        </Typography>
      </Box>
      {!required && (
        <Button
          size="small"
          onClick={onContinueWithout}
          data-testid={`${testIdPrefix}-continue-without`}
          sx={{ minHeight: TOUCH_TARGET_PX }}
        >
          {t('deadlines.aiPreview.continueWithout', { defaultValue: 'Continua senza documento' })}
        </Button>
      )}
    </Box>
  );
}

type CaptionKind = 'error' | 'quota' | 'helper';

interface Caption {
  kind: CaptionKind;
  id: string;
  text: string;
}

interface CaptionInput {
  reading: DeadlineDocumentReading;
  helper?: string;
  disabled: boolean;
  disabledHelper?: string;
  errorText?: string;
  testIdPrefix: string;
  quotaReason: string;
}

/**
 * Una riga sola sotto i pulsanti, tranne l'errore insieme al motivo della quota: l'errore dice cosa
 * manca, il motivo spiega il pulsante IA disattivato (anche subito dopo la lettura che ha usato
 * l'ultima unità). A quota finita il motivo prende il posto dell'aiuto generico.
 */
function resolveCaptions(input: CaptionInput): Caption[] {
  const { reading, helper, disabled, disabledHelper, errorText, testIdPrefix } = input;
  if (reading.status === 'expired') {
    return [];
  }
  const captions: Caption[] = [];
  if (errorText && !disabled) {
    captions.push({ kind: 'error', id: `${testIdPrefix}-error`, text: errorText });
  }
  if (reading.aiQuotaExhausted && !disabled) {
    captions.push({
      kind: 'quota',
      id: `${testIdPrefix}-ai-quota-reason`,
      text: input.quotaReason,
    });
  }
  const shownHelper = disabled ? (disabledHelper ?? helper) : helper;
  if (captions.length === 0 && shownHelper) {
    captions.push({ kind: 'helper', id: `${testIdPrefix}-helper`, text: shownHelper });
  }
  return captions;
}

function CaptionLine({ caption }: { caption: Caption }) {
  const isError = caption.kind === 'error';
  return (
    <Typography
      id={caption.id}
      variant="caption"
      role={isError ? 'alert' : undefined}
      data-testid={caption.kind === 'helper' ? undefined : caption.id}
      sx={{ color: isError ? 'error.main' : 'text.secondary' }}
    >
      {caption.text}
    </Typography>
  );
}

/** `secondary`: il solo "Carica documento" è l'azione principale, la lettura IA resta un'alternativa discreta. */
type AiButtonVariant = 'primary' | 'secondary';

interface EmptyDocumentProps {
  reading: DeadlineDocumentReading;
  labelId: string;
  helper?: string;
  disabled: boolean;
  disabledHelper?: string;
  required: boolean;
  errorText?: string;
  aiButtonVariant: AiButtonVariant;
  testIdPrefix: string;
  onPick: (file: File, read: boolean) => void;
}

/** Nessun file: i due pulsanti di caricamento, una riga di aiuto o di errore, lo stato del token. */
function EmptyDocument(props: EmptyDocumentProps) {
  const { reading, labelId, disabled, required, aiButtonVariant, testIdPrefix, onPick } = props;
  const isAiSecondary = aiButtonVariant === 'secondary';
  const { t } = useTranslation('documents');
  const captions = resolveCaptions({
    ...props,
    quotaReason: t('deadlines.aiPreview.quotaExhaustedReason', {
      defaultValue:
        'Lettura automatica esaurita questa settimana: carica il documento e compila a mano',
    }),
  });
  const describedBy = captions.map((caption) => caption.id).join(' ') || undefined;
  const hasError = captions.some((caption) => caption.kind === 'error');
  return (
    <>
      {/* Affiancati a pari larghezza; sotto i 460px (due colonne da 220px non ci stanno) vanno uno sotto l'altro. */}
      <Box
        role="group"
        aria-labelledby={labelId}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 1,
        }}
      >
        <UploadButton
          label={t('deadlines.aiPreview.uploadOnly', { defaultValue: 'Carica documento' })}
          icon={<UploadFileOutlinedIcon />}
          disabled={disabled}
          hasError={hasError}
          testId={`${testIdPrefix}-plain-file-input`}
          describedBy={describedBy}
          onPick={(file) => onPick(file, false)}
        />
        {isAiSecondary ? (
          <UploadButton
            label={t('deadlines.aiPreview.uploadAndReadSecondary', {
              defaultValue: "Oppure: carica e compila con l'IA",
            })}
            icon={<AutoAwesomeOutlinedIcon />}
            variant="text"
            disabled={disabled || reading.aiQuotaExhausted}
            hasError={false}
            testId={`${testIdPrefix}-file-input`}
            describedBy={describedBy}
            onPick={(file) => onPick(file, true)}
          />
        ) : (
          <UploadButton
            label={t('deadlines.aiPreview.uploadAndRead', {
              defaultValue: "Carica e compila con l'IA",
            })}
            icon={<AutoAwesomeOutlinedIcon />}
            disabled={disabled || reading.aiQuotaExhausted}
            hasError={hasError}
            testId={`${testIdPrefix}-file-input`}
            describedBy={describedBy}
            onPick={(file) => onPick(file, true)}
          />
        )}
      </Box>
      {captions.map((caption) => (
        <CaptionLine key={caption.kind} caption={caption} />
      ))}
      {reading.status === 'expired' ? (
        <ExpiredDocumentNotice
          required={required}
          testIdPrefix={testIdPrefix}
          onContinueWithout={reading.discard}
        />
      ) : (
        <ReadingStatusLine reading={reading} />
      )}
    </>
  );
}

interface AttachedDocumentProps {
  reading: DeadlineDocumentReading;
  file: File;
  labelId: string;
  disabled: boolean;
  testIdPrefix: string;
  onPick: (file: File, read: boolean) => void;
  onReplace: (file: File) => void;
}

/** File scelto: la pillola al posto dei pulsanti, lo stato, "Sostituisci" e l'avviso art. 50. */
function AttachedDocument({
  reading,
  file,
  labelId,
  disabled,
  testIdPrefix,
  onPick,
  onReplace,
}: AttachedDocumentProps) {
  const { t } = useTranslation('documents');
  const busy = isBusy(reading.status);
  const offersAiRead = !disabled && canOfferAiRead(reading, file);
  return (
    <>
      <DocumentFilePill
        file={file}
        isBusy={busy}
        disabled={disabled}
        labelId={labelId}
        testIdPrefix={testIdPrefix}
        onRemove={reading.discard}
      />
      <ReadingStatusLine reading={reading} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          component="label"
          size="small"
          startIcon={<UploadFileOutlinedIcon />}
          disabled={disabled || busy}
          sx={{ minHeight: TOUCH_TARGET_PX }}
        >
          {t('deadlines.aiPreview.replaceFile', { defaultValue: 'Sostituisci' })}
          <HiddenFileInput
            disabled={disabled || busy}
            testId={`${testIdPrefix}-plain-file-input`}
            onPick={onReplace}
          />
        </Button>
        {offersAiRead && (
          <Button
            size="small"
            startIcon={<AutoAwesomeOutlinedIcon />}
            onClick={() => onPick(file, true)}
            data-testid={`${testIdPrefix}-read-attached`}
            sx={{ minHeight: TOUCH_TARGET_PX }}
          >
            {t('deadlines.aiPreview.readAttached', { defaultValue: "Compila con l'IA" })}
          </Button>
        )}
      </Box>
      {isDisclaimerVisible(reading.status) && <AiActDisclaimer />}
    </>
  );
}

interface DeadlineDocumentFieldProps {
  reading: DeadlineDocumentReading;
  label: string;
  helper?: string;
  disabled?: boolean;
  /** Mostrato al posto di `helper` quando il campo è disattivato. */
  disabledHelper?: string;
  /** Senza documento non si salva: asterisco nella label e, a token scaduto, solo il nuovo caricamento. */
  required?: boolean;
  /** Cosa manca, in rosso al posto di `helper` (campo obbligatorio, dopo un'interazione). */
  errorText?: string;
  aiButtonVariant?: AiButtonVariant;
  testIdPrefix: string;
}

/**
 * Senza file: "Carica documento" allega soltanto (nessuna quota), "Carica e compila con l'IA" legge
 * il file e propone i campi; a quota settimanale finita il secondo resta visibile, disattivato con il
 * motivo. Con il file: una pillola con il nome e la X per toglierlo, al posto dei pulsanti.
 */
export function DeadlineDocumentField({
  reading,
  label,
  helper,
  disabled = false,
  disabledHelper,
  required = false,
  errorText,
  aiButtonVariant = 'primary',
  testIdPrefix,
}: DeadlineDocumentFieldProps) {
  const labelId = `${testIdPrefix}-label`;
  function pick(file: File, read: boolean) {
    reading.pickFile(file, { read });
  }
  /**
   * "Sostituisci" allega soltanto: la quota IA non si spende mai senza dirlo. Sul nuovo file resta
   * "Compila con l'IA", esplicito.
   */
  function replace(file: File) {
    pick(file, false);
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography id={labelId} variant="body2" sx={{ fontWeight: 600 }}>
        {required ? `${label} *` : label}
      </Typography>
      {reading.file ? (
        <AttachedDocument
          reading={reading}
          file={reading.file}
          labelId={labelId}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
          onPick={pick}
          onReplace={replace}
        />
      ) : (
        <EmptyDocument
          reading={reading}
          labelId={labelId}
          helper={helper}
          disabled={disabled}
          disabledHelper={disabledHelper}
          required={required}
          errorText={errorText}
          aiButtonVariant={aiButtonVariant}
          testIdPrefix={testIdPrefix}
          onPick={pick}
        />
      )}
    </Box>
  );
}
